import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { sendPlaylistEvent } from '../../utils/playlistEvents';

export default function TextBlock({ block, playlist, blockIndex, onComplete, persistentCTA }) {
  const [watchStartTime] = useState(Date.now());
  const text = block.props?.text || block.props?.content || '';
  const duration = block.props?.duration || 4; // Default 4 seconds

  useEffect(() => {
    sendPlaylistEvent(playlist.id, 'block_start', {
      block_id: block.id,
      block_index: blockIndex,
      block_type: 'text'
    });

    // Auto-advance after duration
    const timer = setTimeout(() => {
      sendPlaylistEvent(playlist.id, 'block_complete', {
        block_id: block.id,
        block_index: blockIndex,
        watch_ms: Date.now() - watchStartTime
      });
      if (onComplete) onComplete();
    }, duration * 1000);

    return () => clearTimeout(timer);
  }, [block.id, blockIndex, duration, onComplete, playlist.id, watchStartTime]);

  const handleCTAClick = useCallback((e) => {
    e.stopPropagation();
    sendPlaylistEvent(playlist.id, 'cta_click', {
      block_id: block.id,
      block_index: blockIndex,
      url: persistentCTA?.url,
      label: persistentCTA?.label
    });
    if (persistentCTA?.url) {
      window.open(persistentCTA.url, '_blank', 'noopener,noreferrer');
    }
  }, [playlist.id, block.id, blockIndex, persistentCTA]);

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center p-8">
      {/* Text with slide-up animation */}
      <div className="max-w-2xl text-center animate-slide-up">
        <p className="text-white text-2xl md:text-4xl font-bold leading-relaxed whitespace-pre-wrap">
          {text}
        </p>
      </div>

      {/* Persistent CTA */}
      {persistentCTA && (
        <div 
          className="absolute bottom-0 left-0 right-0 p-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <Button
            onClick={handleCTAClick}
            className="w-full bg-white text-black hover:bg-gray-200 font-semibold"
            size="lg"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {persistentCTA.label}
          </Button>
        </div>
      )}
    </div>
  );
}
